// Swipe between a product card's primary and secondary image on touch devices (desktop uses hover instead).
// The images slide with the finger like a two-slide carousel and snap to the nearest one on release.
// Listeners are delegated to the document so cards added later (filtering, pagination) work without re-binding.

const touchDevice = window.matchMedia('(hover: none) and (pointer: coarse)');
const DIRECTION_LOCK_DISTANCE = 8;
const SWITCH_RATIO = 0.2;
const FLICK_VELOCITY = 0.4; // px per ms

let swipe = null;
let suppressClickUntil = 0;

function swipeableMedia(target) {
  const media = target.closest('.product-card__media');

  if (!media || !media.querySelector('.product-card__image--secondary') || media.closest('scroll-carousel')) {
    return null;
  }

  return media;
}

function endSwipe(touch) {
  const { media, startX, startTime, width, dragging } = swipe;
  swipe = null;

  if (!dragging) return;

  const deltaX = touch ? touch.clientX - startX : 0;
  const velocity = Math.abs(deltaX) / (Date.now() - startTime);
  const showingSecondary = media.classList.contains('is-showing-secondary');
  const towardsOther = showingSecondary ? deltaX > 0 : deltaX < 0;
  const shouldSwitch = towardsOther && (Math.abs(deltaX) > width * SWITCH_RATIO || velocity > FLICK_VELOCITY);

  media.classList.remove('is-dragging');
  media.style.removeProperty('--swipe-drag');
  media.classList.toggle('is-showing-secondary', shouldSwitch ? !showingSecondary : showingSecondary);
  suppressClickUntil = Date.now() + 400;
}

document.addEventListener('touchstart', (event) => {
  swipe = null;

  if (!touchDevice.matches || event.touches.length !== 1) return;

  const media = swipeableMedia(event.target);
  if (!media) return;

  const touch = event.touches[0];
  swipe = { media, startX: touch.clientX, startY: touch.clientY, startTime: Date.now(), width: media.clientWidth, dragging: false };
}, { passive: true });

document.addEventListener('touchmove', (event) => {
  if (!swipe) return;

  const touch = event.touches[0];
  const deltaX = touch.clientX - swipe.startX;
  const deltaY = touch.clientY - swipe.startY;

  if (!swipe.dragging) {
    if (Math.abs(deltaX) < DIRECTION_LOCK_DISTANCE && Math.abs(deltaY) < DIRECTION_LOCK_DISTANCE) return;

    // Mostly vertical movement is a page scroll, so let it go
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      swipe = null;
      return;
    }

    swipe.dragging = true;
    swipe.media.classList.add('is-dragging');
  }

  // Only allow dragging towards the other image, with a little resistance past the ends
  const showingSecondary = swipe.media.classList.contains('is-showing-secondary');
  const min = showingSecondary ? 0 : -swipe.width;
  const max = showingSecondary ? swipe.width : 0;
  const drag = deltaX < min ? min + (deltaX - min) / 4 : deltaX > max ? max + (deltaX - max) / 4 : deltaX;

  swipe.media.style.setProperty('--swipe-drag', `${drag}px`);
}, { passive: true });

document.addEventListener('touchend', (event) => {
  if (swipe) endSwipe(event.changedTouches[0]);
}, { passive: true });

document.addEventListener('touchcancel', () => {
  if (swipe) endSwipe(null);
}, { passive: true });

// A swipe must not also follow the card's link
document.addEventListener('click', (event) => {
  if (Date.now() < suppressClickUntil && event.target.closest('.product-card__media')) {
    event.preventDefault();
    event.stopPropagation();
  }
}, true);

// The secondary image sits off to the side (clipped), so native lazy loading would never fetch it.
// Load it once its card gets close to the viewport instead.
if (touchDevice.matches) {
  const preloadObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.querySelector('.product-card__image--secondary').loading = 'eager';
      preloadObserver.unobserve(entry.target);
    });
  }, { rootMargin: '200px' });

  const observeCards = (root) => {
    root.querySelectorAll('.product-card__media').forEach((media) => {
      if (swipeableMedia(media)) preloadObserver.observe(media);
    });
  };

  observeCards(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) observeCards(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
}
